import {
    type FC
} from "react";
import {
    Image,
    View
} from "react-native";
import type IAttachmentMediaProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    AttachmentPartContentProps
} from "../../type";
import {
    CircleAlert as CircleAlertIcon
} from "lucide-react-native";
import {
    withAttachmentContext
} from "../../context";
import AttachmentMediaBadge from "../mediaBadge";
import Progress from "../../../progress";
import Icon from "../../../icon";

const AttachmentMediaContent: FC<AttachmentPartContentProps<IAttachmentMediaProps>> = ({
    customTheme,
    children,
    context,
    style,
    ...props
}) => {
    const currentTheme = customTheme ?? context.customTheme;

    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const {
        container: containerDynamicStyle,
        badge: badgeDynamicStyle,
        disc: discDynamicStyle
    } = useStyles({
        shapeConfigs,
        shadowAlpha,
        elevations,
        context,
        colors,
        spaces
    });

    const isProgress = context.uploadState === "uploading" || context.uploadState === "processing";

    const renderIcon = (icon = context.icon) => {
        return <Icon
            customColor={context.currentColors.mediaIconColor}
            size={context.currentSize.mediaIconSize}
            customTheme={currentTheme}
            icon={icon}
        />;
    };

    const renderRing = () => {
        const ring = <Progress
            title={localize(context.uploadState === "uploading" ? "attachment-uploading" : "attachment-processing", [
                context.title ?? ""
            ])}
            isIndeterminate={context.uploadState === "processing" || context.progress === undefined}
            customLocalize={context.customLocalize}
            value={context.progress ?? 0}
            customTheme={currentTheme}
            size={context.size}
            variant="circular"
        >
            <Progress.Track>
                <Progress.Indicator />
                <View
                    style={stylesheet.center}
                >
                    {renderIcon()}
                </View>
            </Progress.Track>
        </Progress>;

        if(context.orientation === "horizontal") {
            return ring;
        }

        return <View
            style={[
                stylesheet.disc,
                discDynamicStyle
            ]}
        >
            {ring}
        </View>;
    };

    const renderContent = () => {
        if(children) {
            return children;
        }

        if(context.uploadState === "error") {
            return renderIcon(CircleAlertIcon);
        }

        if(context.source) {
            return <Image
                source={context.source}
                style={stylesheet.fill}
                resizeMode="cover"
                aria-hidden={true}
            />;
        }

        return renderIcon();
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {isProgress && context.orientation === "vertical" && context.source ? renderContent() : null}
        {isProgress ? renderRing() : renderContent()}
        {
            context.badge ?
                <AttachmentMediaBadge
                    style={[
                        stylesheet.badge,
                        badgeDynamicStyle
                    ]}
                >
                    {context.badge}
                </AttachmentMediaBadge>
            :
                null
        }
    </View>;
};
export default withAttachmentContext("Media", AttachmentMediaContent);
